import { h, Fragment } from 'preact'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'preact/compat'
import PropTypes from 'prop-types'
import {
  cardSelect,
  cardsUpdateAfterDeletion,
  clearSelection,
  cardsSelector
} from '../../redux/slice/cardsSlice'
import SavedCard from './SavedCard'
import CardsListSelection from './CardsListSelection'
import CardsListDeletion from './CardsListDeletion'
import LinkButton from '../Common/LinkButton'
import './styles.scss'
import CVV from '../CardForm/Fields/CVV'
import { CARD_FIELDS } from '../../constants/card'
import { useTranslations } from '../../hooks/useTranslations'
import { getCards } from '../../api/savedCards'

const SavedCards = ({
  handleAddCard,
  formProps: { register, errors, setValue, trigger },
}) => {
  const dispatch = useDispatch()
  const { selectedCard } = useSelector(cardsSelector)

  const l10n = useTranslations()

  const [isEditMode, setEditMode] = useState(false)

  const handleUseDifferentCard = e => {
    e.preventDefault()
    setValue(CARD_FIELDS.cvv, '')
    dispatch(clearSelection())
    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'clearSelection',
        },
      },
      '*',
    )
  }

  const getUpdatedCards = () =>
    getCards().then(({ responseData }) => {
      dispatch(cardsUpdateAfterDeletion(responseData))
      window.parent?.postMessage?.(
        {
          type: 'MFEStoreDispatch',
          data: {
            actionType: 'cardsUpdateAfterDeletion',
            message: responseData,
          },
        },
        '*',
      )
    })

  const handleDeleteSuccess = async () => {
    setEditMode(false)
    await getUpdatedCards()
  }

  const onChangeCardSelect = cardId => {
    dispatch(cardSelect(cardId))
    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'cardSelect',
          message: cardId
        },
      },
      '*',
    )
  }

  const handleEdit = e => {
    e.preventDefault()
    setEditMode(!isEditMode)
  }

  const editButtonText = isEditMode ? l10n.t('Cancel') : l10n.t('Edit')

  const ListComponent = isEditMode ? (
    <CardsListDeletion onDeleteSuccess={handleDeleteSuccess} />
  ) : (
    <CardsListSelection
      handleNewCard={handleAddCard}
      handleUse={onChangeCardSelect}
    />
  )

  return selectedCard ? (
    <>
      <h5 className="cards-title lead">{l10n.t('Credit / Debit card')}</h5>
      <SavedCard {...selectedCard} />
      <div className="saved-card__secure-code lead">
        <CVV
          register={register}
          errors={errors}
          setValue={setValue}
          cardType={selectedCard.cardBrand?.toLowerCase()}
          label={l10n.t('Security code')}
          trigger={trigger}
        />
      </div>
      <LinkButton
        data-testid="use-different-card"
        id="use-different-card-button"
        title={l10n.t('Use different card')}
        onClick={handleUseDifferentCard}
      />
    </>
  ) : (
    <section className="saved-cards" data-testid="saved-cards-select">
      <header className="saved-cards__header card-header">
        <h5 className="card-header__title">{l10n.t('Saved cards')}</h5>
        <LinkButton
          data-testid="card-edit-button"
          id="card-edit-button"
          title={editButtonText}
          onClick={handleEdit}
        />
      </header>

      {ListComponent}
    </section>
  )
}

SavedCards.propTypes = {
  handleAddCard: PropTypes.func.isRequired,
  formProps: PropTypes.exact({
    register: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    setValue: PropTypes.func.isRequired,
    trigger: PropTypes.func.isRequired,
  }),
}

export default SavedCards
